import { useEffect, useState } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import { CalendarDays, MapPin, User, Tag, Share2, Facebook, Link2, Users2, AlertCircle, MessageSquare, Send, ShieldCheck, ThumbsUp, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatWIT, CONTENT_TYPE_LABELS } from "@/lib/appApi";
import { setArticleMeta, resetMeta } from "@/lib/seo";
import NewsCard, { DemoBadge } from "@/components/public/NewsCard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function ytEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed${u.pathname}`;
    if (u.hostname.includes("youtube.com")) { const id = u.searchParams.get("v"); return id ? `https://www.youtube.com/embed/${id}` : url; }
    if (u.hostname.includes("vimeo.com")) return `https://player.vimeo.com/video${u.pathname}`;
  } catch {}
  return url;
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const { unitMap = {}, topicMap = {} } = useOutletContext() || {};
  const [art, setArt] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArt(null); setNotFound(false);
    api.get(`/public/articles/${slug}`).then((r) => setArt(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (art) setArticleMeta(art, unitMap[art.responsible_unit]?.name);
    return () => resetMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [art]);

  if (notFound) return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <AlertCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
      <h1 className="font-serif text-2xl font-bold text-dlh-green">Artikel tidak ditemukan</h1>
      <Link to="/berita" className="mt-4 inline-block text-dlh-orange font-semibold">Kembali ke Berita DLH</Link>
    </div>
  );
  if (!art) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-gray-500">Memuat…</div>;

  const unit = unitMap[art.responsible_unit];
  const shareUrl = window.location.href;
  const ogUrl = `${window.location.origin}/api/og/${art.slug}?u=${encodeURIComponent(window.location.origin)}`;
  const share = (type) => {
    if (type === "wa") window.open(`https://wa.me/?text=${encodeURIComponent(art.title + " " + ogUrl)}`, "_blank");
    if (type === "fb") window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogUrl)}`, "_blank");
    if (type === "copy") { navigator.clipboard.writeText(shareUrl); toast.success("Tautan disalin"); }
    if (type === "native" && navigator.share) navigator.share({ title: art.title, url: shareUrl }).catch(() => {});
  };

  return (
    <article className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="bg-dlh-orange text-white text-xs font-bold px-3 py-1 rounded uppercase">{CONTENT_TYPE_LABELS[art.content_type]}</span>
        {unit && <Link to={`/bidang/${art.responsible_unit}`} className="bg-dlh-greenPale text-dlh-green text-xs font-semibold px-3 py-1 rounded hover:bg-dlh-greenLight/30">{unit.short_name}</Link>}
        {art.is_demo && <DemoBadge article={art} />}
      </div>

      <h1 className="font-serif text-3xl sm:text-4xl font-extrabold text-dlh-green leading-tight break-words">{art.title}</h1>
      <p className="mt-4 article-lead">{art.summary}</p>

      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500 border-y border-gray-200 py-3">
        {art.author_name && <span className="flex items-center gap-1.5"><User className="h-4 w-4" />{art.author_name}</span>}
        <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{formatWIT(art.published_at)}</span>
        {art.event_location && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" />{art.event_location}</span>}
      </div>

      {art.collaborating_units?.length > 0 && (
        <div className="mt-3 flex items-start gap-2 text-sm text-gray-600 bg-dlh-greenPale rounded-lg p-3">
          <Users2 className="h-4 w-4 mt-0.5 text-dlh-greenMid shrink-0" />
          <span>Unit kolaborasi: {art.collaborating_units.map((c) => unitMap[c]?.short_name || c).join(", ")}</span>
        </div>
      )}

      {art.featured_media_url && (
        <figure className="mt-6">
          <img src={mediaUrl(art.featured_media_url)} alt={art.featured_alt || art.title} className="w-full rounded-lg object-cover" />
          {(art.featured_caption || art.featured_credit) && (
            <figcaption className="mt-2 text-xs text-gray-500">{art.featured_caption} {art.featured_credit && <span className="italic">— {art.featured_credit}</span>}</figcaption>
          )}
        </figure>
      )}

      <div className="article-body rte-content mt-6" data-testid="article-body" dangerouslySetInnerHTML={{ __html: art.body_html }} />

      {art.video_links?.length > 0 && (
        <div className="mt-8 space-y-4">
          {art.video_links.map((v, i) => (
            <div key={i} className="rounded-lg overflow-hidden aspect-video bg-black"><iframe src={ytEmbed(v.url)} title={v.title || "Video"} className="w-full h-full" allowFullScreen /></div>
          ))}
        </div>
      )}

      {art.gallery?.length > 0 && (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {art.gallery.map((g, i) => <img key={i} src={mediaUrl(g.url || g)} alt={g.caption || ""} loading="lazy" className="rounded-lg aspect-square object-cover" />)}
        </div>
      )}

      {art.sources?.length > 0 && (
        <div className="mt-8 text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
          <strong className="text-dlh-green">Sumber informasi:</strong>
          <ul className="list-disc pl-5 mt-1">{art.sources.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      {art.public_attachments?.length > 0 && (
        <div className="mt-6">
          <h3 className="font-bold text-dlh-green mb-2">Lampiran Publik</h3>
          <ul className="space-y-1">{art.public_attachments.map((a, i) => <li key={i}><a href={mediaUrl(a.url || a)} target="_blank" rel="noreferrer" className="text-dlh-orange hover:underline text-sm">{a.name || a.title || "Unduh lampiran"}</a></li>)}</ul>
        </div>
      )}

      {art.topics?.length > 0 && (
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <Tag className="h-4 w-4 text-gray-400" />
          {art.topics.map((t) => <Link key={t} to={`/berita?topic=${t}`} className="text-xs bg-dlh-greenPale text-dlh-green px-3 py-1 rounded-full hover:bg-dlh-greenLight/30">{topicMap[t]?.name || t}</Link>)}
        </div>
      )}

      <div className="mt-8 border-t border-gray-200 pt-5">
        <p className="text-sm font-bold text-dlh-green mb-3 flex items-center gap-2"><Share2 className="h-4 w-4" /> Bagikan</p>
        <div className="flex flex-wrap gap-2">
          <ShareBtn onClick={() => share("wa")} testid="share-wa" className="bg-[#25D366]">WhatsApp</ShareBtn>
          <ShareBtn onClick={() => share("fb")} testid="share-fb" className="bg-[#1877F2]"><Facebook className="h-4 w-4" /> Facebook</ShareBtn>
          <ShareBtn onClick={() => share("copy")} testid="share-copy" className="bg-dlh-green"><Link2 className="h-4 w-4" /> Salin Tautan</ShareBtn>
          {typeof navigator !== "undefined" && navigator.share && <ShareBtn onClick={() => share("native")} testid="share-native" className="bg-dlh-greenMid"><Share2 className="h-4 w-4" /> Lainnya</ShareBtn>}
        </div>
      </div>

      {art.related?.length > 0 && (
        <div className="mt-12">
          <h2 className="font-serif text-2xl font-extrabold text-dlh-green mb-6 pl-4 border-l-4 border-dlh-orange">Artikel Terkait</h2>
          <div className="grid sm:grid-cols-2 gap-6">{art.related.map((r) => <NewsCard key={r.id} article={r} unitMap={unitMap} />)}</div>
        </div>
      )}

      <CommentsSection slug={art.slug} />
    </article>
  );
}

function initials(name) {
  return (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
}

const LIKED_KEY = "papa_liked_comments";
function getLiked() { try { return JSON.parse(localStorage.getItem(LIKED_KEY) || "[]"); } catch { return []; } }
function setLikedStore(arr) { try { localStorage.setItem(LIKED_KEY, JSON.stringify(arr)); } catch {} }

function LikeButton({ comment }) {
  const [likes, setLikes] = useState(comment.likes || 0);
  const [liked, setLiked] = useState(() => getLiked().includes(comment.id));
  const toggle = async () => {
    const next = !liked;
    setLiked(next); setLikes((n) => Math.max(0, n + (next ? 1 : -1)));
    const store = getLiked();
    setLikedStore(next ? [...new Set([...store, comment.id])] : store.filter((x) => x !== comment.id));
    try { const r = await api.post(`/public/comments/${comment.id}/like`, { like: next }); setLikes(r.data.likes); }
    catch { /* revert on failure */ setLiked(!next); setLikes((n) => Math.max(0, n + (next ? -1 : 1))); }
  };
  return (
    <button onClick={toggle} data-testid={`comment-like-${comment.id}`}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors ${liked ? "bg-dlh-green text-white border-dlh-green" : "bg-white text-gray-500 border-gray-200 hover:border-dlh-green hover:text-dlh-green"}`}>
      <ThumbsUp className="h-3.5 w-3.5" /> {likes > 0 ? likes : "Suka"}
    </button>
  );
}

function ReplyForm({ slug, parentId, onDone }) {
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || body.trim().length < 2) { toast.error("Nama dan balasan wajib diisi"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/public/articles/${slug}/comments`, { author_name: name, body, parent_id: parentId });
      if (r.data.status === "visible") { onDone(r.data); toast.success("Balasan Anda telah dikirim"); }
      else { onDone(null); toast.success("Balasan Anda diterima dan akan tampil setelah ditinjau moderator"); }
    } catch { toast.error("Gagal mengirim balasan"); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mt-3 bg-gray-50 rounded-lg p-3" data-testid={`reply-form-${parentId}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Nama Anda" className="bg-white mb-2 h-9" data-testid={`reply-name-${parentId}`} />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={1500} rows={2} placeholder="Tulis balasan Anda…" className="bg-white resize-none" data-testid={`reply-body-${parentId}`} />
      <div className="flex justify-end gap-2 mt-2">
        <button type="button" onClick={() => onDone(undefined)} className="text-xs font-semibold text-gray-500 px-3 py-1.5 rounded-full hover:bg-gray-100">Batal</button>
        <button type="submit" disabled={busy} data-testid={`reply-submit-${parentId}`} className="inline-flex items-center gap-1.5 bg-dlh-green hover:bg-dlh-greenDark disabled:opacity-60 text-white text-xs font-semibold px-4 py-1.5 rounded-full">
          <Send className="h-3.5 w-3.5" /> {busy ? "Mengirim…" : "Kirim Balasan"}
        </button>
      </div>
    </form>
  );
}

function CommentsSection({ slug }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replyOpen, setReplyOpen] = useState(null);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get(`/public/articles/${slug}/comments`, { params: { sort } }).then((r) => setItems(r.data.items)).catch(() => {});
  }, [slug, sort]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || body.trim().length < 2) { toast.error("Nama dan tanggapan wajib diisi"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/public/articles/${slug}/comments`, { author_name: name, body });
      if (r.data.status === "visible") {
        setItems((p) => [r.data, ...p]);
        toast.success("Terima kasih, tanggapan Anda telah dikirim");
      } else {
        toast.success("Tanggapan Anda diterima dan akan tampil setelah ditinjau moderator");
      }
      setName(""); setBody("");
    } catch { toast.error("Gagal mengirim tanggapan"); }
    setSubmitting(false);
  };

  const total = items.reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);

  const onReplyDone = (parentId, newReply) => {
    setReplyOpen(null);
    if (newReply) setItems((p) => p.map((c) => c.id === parentId ? { ...c, replies: [...(c.replies || []), newReply] } : c));
  };

  return (
    <section className="mt-12 border-t border-gray-200 pt-8" data-testid="comments-section">
      <h2 className="font-serif text-2xl font-extrabold text-dlh-green mb-1 flex items-center gap-2">
        <MessageSquare className="h-6 w-6 text-dlh-orange" /> Respon &amp; Tanggapan Pemirsa
      </h2>
      <p className="text-sm text-gray-500 mb-6">{total} tanggapan. Sampaikan pendapat Anda dengan santun.</p>

      <form onSubmit={submit} className="bg-dlh-greenPale rounded-xl p-4 sm:p-5 mb-8" data-testid="comment-form">
        <div className="grid gap-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Nama Anda" data-testid="comment-name-input" className="bg-white" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={1500} rows={3} placeholder="Tulis tanggapan Anda…" data-testid="comment-body-input" className="bg-white resize-none" />
          <div className="flex justify-end">
            <button type="submit" disabled={submitting} data-testid="comment-submit-btn"
              className="inline-flex items-center gap-2 bg-dlh-green hover:bg-dlh-greenDark disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors">
              <Send className="h-4 w-4" /> {submitting ? "Mengirim…" : "Kirim Tanggapan"}
            </button>
          </div>
        </div>
      </form>

      {items.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6" data-testid="comment-empty">Belum ada tanggapan. Jadilah yang pertama menanggapi.</p>
      ) : (
        <>
        <div className="flex items-center gap-2 mb-4" data-testid="comment-sort">
          <span className="text-xs text-gray-400">Urutkan:</span>
          <button onClick={() => setSort("newest")} data-testid="sort-newest"
            className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${sort === "newest" ? "bg-dlh-green text-white border-dlh-green" : "bg-white text-gray-500 border-gray-300 hover:border-dlh-green"}`}>Terbaru</button>
          <button onClick={() => setSort("popular")} data-testid="sort-popular"
            className={`text-xs font-semibold px-3 py-1 rounded-full border transition-colors ${sort === "popular" ? "bg-dlh-green text-white border-dlh-green" : "bg-white text-gray-500 border-gray-300 hover:border-dlh-green"}`}>Terpopuler</button>
        </div>
        <ul className="space-y-5" data-testid="comment-list">
          {items.map((c) => (
            <li key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
              <div className="h-10 w-10 shrink-0 rounded-full bg-dlh-greenMid text-white flex items-center justify-center font-bold text-sm">{initials(c.author_name)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-semibold text-gray-800">{c.author_name}</span>
                  <span className="text-xs text-gray-400">{formatWIT(c.created_at, true)}</span>
                </div>
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-line break-words">{c.body}</p>
                <div className="mt-2 flex items-center gap-3">
                  <LikeButton comment={c} />
                  <button onClick={() => setReplyOpen(replyOpen === c.id ? null : c.id)} data-testid={`comment-reply-toggle-${c.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-dlh-green"><CornerDownRight className="h-3.5 w-3.5" /> Balas</button>
                </div>

                {c.reply && (
                  <div className="mt-3 bg-dlh-greenPale border-l-4 border-dlh-green rounded-r-lg p-3" data-testid={`comment-reply-${c.id}`}>
                    <div className="flex items-center gap-1.5 text-xs font-bold text-dlh-green mb-1">
                      <ShieldCheck className="h-4 w-4" /> Tanggapan DLH{c.reply.by_name ? ` — ${c.reply.by_name}` : ""}
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-line break-words">{c.reply.body}</p>
                    <div className="text-[11px] text-gray-400 mt-1">{formatWIT(c.reply.at, true)}</div>
                  </div>
                )}

                {c.replies?.length > 0 && (
                  <ul className="mt-3 space-y-3 pl-4 border-l-2 border-gray-100" data-testid={`replies-${c.id}`}>
                    {c.replies.map((rc) => (
                      <li key={rc.id} className="flex gap-2.5" data-testid={`comment-${rc.id}`}>
                        <div className="h-8 w-8 shrink-0 rounded-full bg-dlh-greenLight/40 text-dlh-greenDark flex items-center justify-center font-bold text-xs">{initials(rc.author_name)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="font-semibold text-gray-800 text-sm">{rc.author_name}</span>
                            <span className="text-[11px] text-gray-400">{formatWIT(rc.created_at, true)}</span>
                          </div>
                          <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-line break-words">{rc.body}</p>
                          <div className="mt-1.5"><LikeButton comment={rc} /></div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {replyOpen === c.id && <ReplyForm slug={slug} parentId={c.id} onDone={(nr) => onReplyDone(c.id, nr)} />}
              </div>
            </li>
          ))}
        </ul>
        </>
      )}
    </section>
  );
}

function ShareBtn({ onClick, children, className, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-white text-sm font-semibold px-4 py-2 rounded-full hover:opacity-90 focus:ring-2 focus:ring-dlh-orange transition-opacity ${className}`}>
      {children}
    </button>
  );
}
